import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../../utils/format';
import Icon from '../../../components/AppIcon';

/**
 * CategoriesOfWorkersTable
 * ----------------------------------------------------------------------------
 * Directly implements Directive (EU) 2023/970 Art. 9(g): gender pay gap
 * broken down by categories of workers. This is the single most specific
 * reporting requirement in the directive, and the one where most generic
 * HRIS dashboards fall short.
 */

// formatHeadcount replaced with locale-aware num() from useFormatters

const GapBar = ({ value, max }) => {
  const pct = Math.min(100, (value / max) * 100);
  const critical = value > 5;
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            critical ? 'bg-destructive' : value > 3 ? 'bg-warning' : 'bg-success'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs font-semibold tabular-nums w-10 text-right ${
        critical ? 'text-destructive' : 'text-foreground'
      }`}>
        {value.toFixed(1)}%
      </span>
    </div>
  );
};

const FemaleShareDot = ({ share }) => {
  // Visual cue: green near 50%, amber below 35%/above 65%, red below 25%/above 75%
  const distance = Math.abs(share - 50);
  const color =
    distance <= 15 ? 'bg-success'
    : distance <= 25 ? 'bg-warning'
    : 'bg-destructive';
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${color}`} />
      <span className="text-xs tabular-nums text-foreground">{share}%</span>
    </div>
  );
};

const CategoriesOfWorkersTable = ({ data }) => {
  const { t } = useTranslation();
  const { num } = useFormatters();
  const [sortBy, setSortBy] = useState('meanGap');
  const [sortDir, setSortDir] = useState('desc');

  const maxGap = useMemo(
    () => Math.max(...data.map((d) => Math.max(d.meanGap, d.medianGap))),
    [data]
  );

  const sorted = useMemo(() => {
    const arr = [...data];
    arr.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [data, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('desc'); }
  };

  const SortHeader = ({ col, label, align = 'left' }) => (
    <button
      onClick={() => toggleSort(col)}
      className={`flex items-center gap-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-smooth ${
        align === 'right' ? 'ml-auto' : ''
      }`}
    >
      <span>{label}</span>
      {sortBy === col && (
        <Icon name={sortDir === 'asc' ? 'ArrowUp' : 'ArrowDown'} size={10} />
      )}
    </button>
  );

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-foreground">{t('dashboard.categories.title')}</h3>
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-wide">
              Art. 9(g)
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('dashboard.categories.subtitle')}
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-[10px] text-muted-foreground">
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-success" />
            <span>{t('dashboard.categories.legendLow')}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-warning" />
            <span>{t('dashboard.categories.legendMid')}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-destructive" />
            <span>{t('dashboard.categories.legendHigh')}</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/40 border-b border-border">
            <tr>
              <th className="px-5 py-2.5 text-left">
                <SortHeader col="name" label={t('dashboard.categories.tableHeaders.category')} />
              </th>
              <th className="px-3 py-2.5 text-right hidden md:table-cell">
                <SortHeader col="headcount" label={t('dashboard.categories.tableHeaders.headcount')} align="right" />
              </th>
              <th className="px-3 py-2.5 text-right hidden md:table-cell">
                <SortHeader col="femaleShare" label={t('dashboard.categories.tableHeaders.femalePct')} align="right" />
              </th>
              <th className="px-3 py-2.5 text-right min-w-[120px]">
                <SortHeader col="meanGap" label={t('dashboard.categories.tableHeaders.meanGap')} align="right" />
              </th>
              <th className="px-3 py-2.5 text-right min-w-[120px] hidden lg:table-cell">
                <SortHeader col="medianGap" label={t('dashboard.categories.tableHeaders.medianGap')} align="right" />
              </th>
              <th className="px-5 py-2.5 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => (
              <tr
                key={row.name}
                className={`border-b border-border last:border-b-0 hover:bg-muted/30 transition-smooth ${
                  row.meanGap > 5 ? 'bg-destructive/[0.02]' : ''
                }`}
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{row.name}</span>
                    {row.meanGap > 5 && (
                      <span
                        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-destructive/10"
                        title={t('dashboard.categories.exceedsTooltip')}
                      >
                        <Icon name="AlertCircle" size={10} className="text-destructive" />
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-right text-sm text-muted-foreground tabular-nums hidden md:table-cell">
                  {num(row.headcount)}
                </td>
                <td className="px-3 py-3 text-right hidden md:table-cell">
                  <div className="flex justify-end">
                    <FemaleShareDot share={row.femaleShare} />
                  </div>
                </td>
                <td className="px-3 py-3">
                  <GapBar value={row.meanGap} max={maxGap} />
                </td>
                <td className="px-3 py-3 hidden lg:table-cell">
                  <GapBar value={row.medianGap} max={maxGap} />
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-smooth"
                    title={t('dashboard.categories.viewDetail')}
                  >
                    <Icon name="ChevronRight" size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3 bg-muted/40 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <span>{t('dashboard.categories.totalLine', { count: data.length, workers: num(data.reduce((a, b) => a + b.headcount, 0)) })}</span>
        <button className="flex items-center gap-1 hover:text-foreground transition-smooth">
          <Icon name="Download" size={12} />
          <span className="hidden sm:inline">{t('common.exportCsv')}</span>
        </button>
      </div>
    </div>
  );
};

export default CategoriesOfWorkersTable;
